import { useMemo } from 'react';

import { InfoIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  SidebarWrapper,
  SidebarWrapperOverflowContainer,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { useResponsiveSidebarClose } from '@/components/organisms/Sidebar/ResponsiveSidebarPanel';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useRolePermissions } from '@/hooks/useRolePermissions';
import { useSidebar } from '@/hooks/useSidebar';
import { PERMISSION_GROUPS, PERMISSION_LEVEL_LABEL_KEYS } from '@/util/permissionCatalog';
import { permissionsToAccess } from '@/util/roleAccess';

/** Read-only summary of a role's access. System roles have no editable view. */
export const RolePermissionsPanel = () => {
  const { t } = useTranslation();
  const { meta } = useSidebar();
  const close = useResponsiveSidebarClose();

  const { permissions, listStatus } = useRolePermissions(meta?.roleId);

  const levels = useMemo(() => permissionsToAccess(permissions).levels, [permissions]);
  const isLoading = listStatus === 'pending';

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle>
        <span className="line-clamp-1 flex-1 text-base leading-none font-medium text-foreground">
          {t('roles.permissions_of', { 0: meta?.roleName ?? '' })}
        </span>
        <Button
          variant="ghost-neutral"
          size="icon-sm"
          aria-label={t('common.close')}
          onClick={close}
        >
          <XIcon size={16} />
        </Button>
      </SidebarWrapperTitle>

      <SidebarWrapperOverflowContainer className="p-4">
        {PERMISSION_GROUPS.map((group) => (
          <div key={group.key} className="flex">
            <div className="flex h-14 flex-1 items-center pr-4 pl-1">
              <span className="text-sm leading-5 font-semibold text-foreground">
                {t(group.labelKey)}
              </span>
            </div>
            <div className="flex h-14 w-50 items-center justify-end px-4">
              {isLoading ? (
                <Skeleton className="h-5 w-28" />
              ) : (
                <span className="text-sm leading-5 text-foreground-muted">
                  {t(PERMISSION_LEVEL_LABEL_KEYS[levels[group.key]])}
                </span>
              )}
            </div>
          </div>
        ))}

        <Alert variant="info" className="mt-2 [&>svg]:text-informational">
          <InfoIcon />
          <AlertDescription className="max-w-md leading-5">
            {t('roles.system_roles_read_only')}
          </AlertDescription>
        </Alert>
      </SidebarWrapperOverflowContainer>
    </SidebarWrapper>
  );
};
